import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, RefreshCw, Download, Printer, Eye, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { REPORT_AMOUNTS, REPORT_GROUPINGS, buildReportRows, filterReportRows, groupReportRows, summarizeReport } from '../payroll/core/payrollReport';
import { loadApprovedPayroll } from '../payroll/data/reportActions';
import { listTemplates } from '../payroll/data/periodActions';
import { exportPayrollReport } from '../payroll/data/exportPayroll';
import { PAYROLL_PATHS, canViewPayrollPage } from '../payroll/data/permissions';
import {
    AccessDenied, EmptyState, FilterField, IconAction, ListToolbar, PageShell, Pagination, SortableTh, Spinner, StatCards, buttonSecondary,
    formatMoney, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, usePagination, useSortedRows,
} from '../payroll/components/ui';

// Cột đầu của bảng theo từng cách xem: [tiêu đề, khóa sắp xếp].
const LEADING = {
    detail: [['Kỳ', 'period'], ['Nhân sự', 'label'], ['Tổ', 'team']],
    month: [['Tháng', 'period'], ['Số nhân sự', 'employees']],
    team: [['Tổ', 'label'], ['Số nhân sự', 'employees'], ['Số kỳ', 'periods']],
    employee: [['Nhân sự', 'label'], ['Tổ', 'team'], ['Số kỳ', 'periods']],
};
const DEFAULT_SORT = {
    detail: { key: 'period', direction: 'desc' },
    month: { key: 'period', direction: 'desc' },
    team: { key: 'label', direction: 'asc' },
    employee: { key: 'label', direction: 'asc' },
};

const BaoCaoCongLuong = () => {
    const navigate = useNavigate();
    const canView = canViewPayrollPage(PAYROLL_PATHS.reports);
    const [year, setYear] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [templateNames, setTemplateNames] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [groupBy, setGroupBy] = useState('detail');
    const [search, setSearch] = useState('');
    const [month, setMonth] = useState('ALL');
    const [team, setTeam] = useState('ALL');
    const [template, setTemplate] = useState('ALL');

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            const [payroll, templates] = await Promise.all([loadApprovedPayroll(year), listTemplates()]);
            setData(payroll);
            setTemplateNames(new Map(templates.map((item) => [item.MA_MAU, item.TEN_MAU])));
        } catch (error) {
            console.error('Lỗi tải báo cáo công lương:', error);
            toast.error('Không tải được số liệu lương đã duyệt');
        } finally {
            setLoading(false);
        }
    }, [year]);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const allRows = useMemo(() => (data ? buildReportRows(data.periods, data.details) : []), [data]);
    const years = useMemo(() => [...new Set([new Date().getFullYear(), year, ...(data?.periods ?? []).map((period) => period.NAM)])].sort((a, b) => b - a), [data, year]);
    const teams = useMemo(() => [...new Set(allRows.map((row) => row.to))].sort((a, b) => a.localeCompare(b, 'vi')), [allRows]);
    const usedTemplates = useMemo(() => [...new Set(allRows.map((row) => row.templateId))], [allRows]);
    const filteredRows = useMemo(() => filterReportRows(allRows, { month, team, template })
        .filter((row) => matchesSearch(`${row.hoTen} ${row.soHd} ${row.to}`, search)), [allRows, month, team, template, search]);
    const grouped = useMemo(() => groupReportRows(filteredRows, groupBy), [filteredRows, groupBy]);
    const totals = useMemo(() => summarizeReport(filteredRows), [filteredRows]);
    const accessors = useMemo(() => ({
        period: (row) => row.nam * 100 + row.thang, label: (row) => row.label, team: (row) => row.to, employees: (row) => row.employeeCount, periods: (row) => row.periodCount,
        ...Object.fromEntries(REPORT_AMOUNTS.map(({ key }) => [key, (row) => row[key]])),
    }), []);
    const { sorted, sort, toggleSort, setSort } = useSortedRows(grouped, accessors, DEFAULT_SORT.detail);
    const { pageRows, pagination } = usePagination(sorted, 50);

    if (!canView) return <AccessDenied />;

    const changeGrouping = (value) => {
        setGroupBy(value);
        setSort(DEFAULT_SORT[value]);
    };
    // Bấm một nhóm để xem các dòng chi tiết của nhóm đó.
    const drillDown = (row) => {
        if (groupBy === 'month') setMonth(String(row.thang));
        if (groupBy === 'team') setTeam(row.key);
        if (groupBy === 'employee') setSearch(row.soHd);
        changeGrouping('detail');
    };
    const title = `Báo cáo công lương ${month === 'ALL' ? `năm ${year}` : `tháng ${month}/${year}`}${team === 'ALL' ? '' : ` - ${team}`}`;
    const handleExport = () => exportPayrollReport({ title, groupBy, rows: sorted, totals, amounts: REPORT_AMOUNTS })
        .catch((error) => toast.error(`Không xuất được Excel: ${error.message}`));

    const leadingCells = (row) => {
        const person = (
            <td className={tdClass}>
                <div className="font-medium text-gray-900">{row.label}</div>
                <div className="text-xs text-gray-500">{row.sub}</div>
            </td>
        );
        if (groupBy === 'detail') return (<><td className={`${tdClass} whitespace-nowrap`}>{row.periodLabel}</td>{person}<td className={tdClass}>{row.to}</td></>);
        if (groupBy === 'month') return (<><td className={`${tdClass} font-medium text-gray-900 whitespace-nowrap`}>{row.label}</td><td className={tdClass}>{row.employeeCount}</td></>);
        if (groupBy === 'team') return (<><td className={`${tdClass} font-medium text-gray-900`}>{row.label}</td><td className={tdClass}>{row.employeeCount}</td><td className={tdClass}>{row.periodCount}</td></>);
        return (<>{person}<td className={tdClass}>{row.to}</td><td className={tdClass}>{row.periodCount}</td></>);
    };

    return (
        <PageShell icon={BarChart3} title="Báo cáo công lương" subtitle="Tổng hợp số đã chốt của các kỳ lương đã duyệt, theo tháng, tổ và nhân sự"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    <button className={buttonSecondary} onClick={() => window.print()} disabled={!sorted.length}><Printer className="w-4 h-4" />In</button>
                    <button className={buttonSecondary} onClick={handleExport} disabled={!sorted.length}><Download className="w-4 h-4" />Xuất Excel</button>
                </>
            )}>
            <StatCards cards={[
                { key: 'periods', label: 'Kỳ đã duyệt', value: totals.periodCount, tone: 'indigo', caption: `Năm ${year}` },
                { key: 'employees', label: 'Nhân sự', value: totals.employeeCount, tone: 'green', caption: `${filteredRows.length} dòng lương` },
                { key: 'gross', label: 'Tổng cộng', value: `${formatMoney(totals.gross)} đ`, tone: 'yellow', caption: `Trừ ứng ${formatMoney(totals.advance)} đ` },
                { key: 'net', label: 'Tổng thực chi', value: `${formatMoney(totals.net)} đ`, tone: 'purple', caption: `BH NLĐ ${formatMoney(totals.insurance)} đ · TNCN ${formatMoney(totals.tax)} đ` },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm tên, số HĐ hoặc tổ..."
                filterCount={[month, team, template].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setMonth('ALL'); setTeam('ALL'); setTemplate('ALL'); setSearch(''); }}
                filters={(
                    <>
                        <FilterField label="Tháng">
                            <select className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="ALL">Cả năm</option>
                                {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Tổ">
                            <select className={inputClass} value={team} onChange={(e) => setTeam(e.target.value)}>
                                <option value="ALL">Tất cả tổ</option>
                                {teams.map((item) => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Mẫu lương">
                            <select className={inputClass} value={template} onChange={(e) => setTemplate(e.target.value)}>
                                <option value="ALL">Tất cả mẫu</option>
                                {usedTemplates.map((item) => <option key={item} value={item}>{templateNames.get(item) || item}</option>)}
                            </select>
                        </FilterField>
                    </>
                )}
                extra={(
                    <>
                        <select className={`${inputClass} w-auto`} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Năm">
                            {years.map((item) => <option key={item} value={item}>Năm {item}</option>)}
                        </select>
                        <div className="flex border border-gray-300 rounded-lg overflow-hidden shadow-sm">
                            {REPORT_GROUPINGS.map(({ value, label }) => (
                                <button key={value} type="button" onClick={() => changeGrouping(value)}
                                    className={`px-3 py-2 text-sm whitespace-nowrap border-r border-gray-300 last:border-r-0 ${groupBy === value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </>
                )} />

            {loading && !data ? <Spinner label="Đang tải số liệu lương đã duyệt..." /> : !sorted.length ? (
                <EmptyState icon={BarChart3} title="Chưa có số liệu lương đã duyệt">
                    Báo cáo chỉ lấy số đã chốt của các kỳ đã duyệt. Đổi năm hoặc bộ lọc, hoặc duyệt kỳ lương ở trang Bảng công lương.
                </EmptyState>
            ) : (
                <>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <th className={`${thClass} text-center w-12`}>STT</th>
                                    {LEADING[groupBy].map(([label, key]) => <SortableTh key={key} label={label} sortKey={key} sort={sort} onSort={toggleSort} />)}
                                    {REPORT_AMOUNTS.map(({ key, label }) => <SortableTh key={key} label={label} sortKey={key} sort={sort} onSort={toggleSort} align="right" />)}
                                    <th className={`${thClass} text-center print:hidden`}>Xem</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((row, index) => (
                                    <tr key={row.key} className="hover:bg-gray-50">
                                        <td className={`${tdClass} text-center text-gray-400`}>{pagination.start + index + 1}</td>
                                        {leadingCells(row)}
                                        {REPORT_AMOUNTS.map(({ key }) => (
                                            <td key={key} className={`${tdClass} text-right whitespace-nowrap ${key === 'net' ? 'font-semibold text-gray-900' : ''}`}>{formatMoney(row[key])}</td>
                                        ))}
                                        <td className={`${tdClass} print:hidden`}>
                                            <div className="flex justify-center">
                                                {groupBy === 'detail'
                                                    ? <IconAction icon={Eye} title="Mở bảng lương" tone="blue" onClick={() => navigate(`/bangcongluong/${row.maKy}`)} />
                                                    : <IconAction icon={Eye} title="Xem chi tiết" tone="blue" onClick={() => drillDown(row)} />}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 font-semibold text-gray-900 text-sm">
                                    <td className="px-3 py-3 text-center" colSpan={1 + LEADING[groupBy].length}>TỔNG CỘNG ({sorted.length})</td>
                                    {REPORT_AMOUNTS.map(({ key }) => <td key={key} className="px-3 py-3 text-right whitespace-nowrap">{formatMoney(totals[key])}</td>)}
                                    <td className="print:hidden" />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div className="print:hidden"><Pagination pagination={pagination} unit="dòng" /></div>
                    <p className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                        <ShieldCheck className="w-4 h-4" />Số liệu lấy từ các kỳ đã duyệt, không đổi khi cấu hình, thỏa thuận hay năng suất thay đổi sau này. Kỳ được mở lại sẽ rời khỏi báo cáo đến khi duyệt lại.
                    </p>
                </>
            )}
        </PageShell>
    );
};

export default BaoCaoCongLuong;
